import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { CollapsibleSection } from '@ooh/ui/components/collapsible-section';
import { FieldRow } from '@ooh/ui/components/field-row';
import { maskFieldValue } from '@ooh/ui/components/masked-value';
import { formatPrice } from '@ooh/ui/lib/media-unit-formatters';
import type { DetailViewUnit } from './detail-view-unit';

// `withheld` MUST be the marker for THIS field's own dimension (`costsWithheld`, `rentWithheld`),
// never another dimension's: the permissions resolve independently (field-visibility).
// Both markers are absent on the client payload, so `?? false` defaults to "not withheld".

function formatCostValue(value: number | null | undefined): ReactNode {
  return <span className="font-mono text-xs">{formatPrice(value ?? null)}</span>;
}

export function CostsCard({
  unit,
  t,
  maskedExplanation,
}: {
  unit: DetailViewUnit;
  t: TFunction<'inventory', undefined>;
  maskedExplanation: string;
}) {
  const costsWithheld = unit.costsWithheld ?? false;
  const rentWithheld = unit.rentWithheld ?? false;
  return (
    <CollapsibleSection
      testId="costs-card"
      toggleTestId="costs-toggle"
      trigger={
        <span className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('detail.sections.costs')}
        </span>
      }
      defaultOpen
    >
      <div className="divide-y divide-border/40 px-4">
        <FieldRow testId="field-rent" label={t('detail.fields.rent')}>
          {maskFieldValue(unit.rent, rentWithheld, maskedExplanation, formatCostValue(unit.rent))}
        </FieldRow>
        <FieldRow testId="field-exposureCost" label={t('detail.fields.exposureCost')}>
          {maskFieldValue(
            unit.exposureCost,
            costsWithheld,
            maskedExplanation,
            formatCostValue(unit.exposureCost),
          )}
        </FieldRow>
        <FieldRow testId="field-installCost" label={t('detail.fields.installCost')}>
          {maskFieldValue(
            unit.installCost,
            costsWithheld,
            maskedExplanation,
            formatCostValue(unit.installCost),
          )}
        </FieldRow>
        <FieldRow testId="field-dismantlingCost" label={t('detail.fields.dismantlingCost')}>
          {maskFieldValue(
            unit.dismantlingCost,
            costsWithheld,
            maskedExplanation,
            formatCostValue(unit.dismantlingCost),
          )}
        </FieldRow>
        <FieldRow testId="field-productionCost" label={t('detail.fields.productionCost')}>
          {maskFieldValue(
            unit.productionCost,
            costsWithheld,
            maskedExplanation,
            formatCostValue(unit.productionCost),
          )}
        </FieldRow>
        <FieldRow testId="field-additionalCosts" label={t('detail.fields.additionalCosts')}>
          {maskFieldValue(
            unit.additionalCosts,
            costsWithheld,
            maskedExplanation,
            formatCostValue(unit.additionalCosts),
          )}
        </FieldRow>
      </div>
    </CollapsibleSection>
  );
}

export function NotesCard({
  unit,
  t,
}: {
  unit: DetailViewUnit;
  t: TFunction<'inventory', undefined>;
}) {
  return (
    <Card data-testid="notes-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-[13px] uppercase tracking-wide text-muted-foreground font-semibold">
          {t('detail.sections.notes')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div data-testid="field-notes" className="text-[13px]">
          {unit.notes ? (
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">{unit.notes}</p>
          ) : (
            <p className="text-muted-foreground italic">{t('detail.noNotes')}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
